import { ReactElement, useState } from "react";
import type { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import {
  Activity,
  AirVent,
  BatteryCharging,
  BellRing,
  Building2,
  CircuitBoard,
  ClipboardCheck,
  CloudLightning,
  DoorOpen,
  Flame,
  Gauge,
  PlugZap,
  ShieldCheck,
  Snowflake,
  Sun,
  Thermometer,
  ThermometerSun,
  Wind,
} from "lucide-react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { ArrowLinkButton } from "~/components/ArrowLinkButton";
import { FadeIn } from "~/components/animations";
import { Banner } from "~/components/Banner";
import SolutionSelector from "~/components/SolutionSelector";
import { MainLayout } from "~/components/layouts/MainLayout";
import { MarginLayout } from "~/components/layouts/MarginLayout";
import { CITIES, CITY_COORDINATES, findCityByName, getCityBySlug } from "~/lib/cities";
import { getCityPageData } from "~/lib/cityData";
import type { CityMarker } from "~/components/PolandMap";

const PolandMap = dynamic(() => import("~/components/PolandMap"), { ssr: false });
import { getCityFaqs } from "~/lib/cityFaqs";
import { SERVICES } from "~/lib/services";
import { getAvailableCityServices } from "~/lib/firms.server";
import { SafetyFact } from "~/components/SafetyFact";
import { getFactForPage } from "~/lib/facts";
import { NextPageWithLayout } from "~/pages/_app";

type CityPageProps = {
  cityName: string;
  cityLocative: string;
  citySlug: string;
  fact: string;
  availableCityServiceSlugs: string[];
};

const ICON_BY_SERVICE = {
  "plug-zap": PlugZap,
  "gauge": Gauge,
  "cloud-lightning": CloudLightning,
  "sun": Sun,
  "battery-charging": BatteryCharging,
  "circuit-board": CircuitBoard,
  "flame": Flame,
  "activity": Activity,
  "thermometer": Thermometer,
  "clipboard-check": ClipboardCheck,
  "wind": Wind,
  "air-vent": AirVent,
  "snowflake": Snowflake,
  "thermometer-sun": ThermometerSun,
  "bell-ring": BellRing,
  "shield-check": ShieldCheck,
  "door-open": DoorOpen,
  "building-2": Building2,
};

const CityPage: NextPageWithLayout<CityPageProps> = ({
  cityName,
  cityLocative,
  citySlug,
  fact,
  availableCityServiceSlugs,
}) => {
  const router = useRouter();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const cityUrl = `https://inspektomat.pl/${citySlug}/`;
  const cityData = getCityPageData(citySlug);
  const faqs = getCityFaqs(citySlug);
  const allCityMarkers: CityMarker[] = CITIES.map((c) => {
    const coords = CITY_COORDINATES[c.slug];
    return { name: c.name, lat: coords.lat, lng: coords.lng };
  });
  const handleCityClick = (city: CityMarker) => {
    const found = findCityByName(city.name);
    if (found) router.push(`/${found.slug}/`);
  };
  const cityServices = SERVICES.filter(
    (service) =>
      !service.cityPages || availableCityServiceSlugs.includes(service.slug)
  ).map((service) => ({
    ...service,
    icon: ICON_BY_SERVICE[service.icon],
    cityHref: service.cityPages ? `/${service.slug}/${citySlug}/` : service.href,
  }));

  return (
    <>
      {faqs.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map(({ q, a }) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            }),
          }}
        />
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `Przegląd instalacji ${cityLocative}`,
            description:
              `Profesjonalne przeglądy instalacji elektrycznych, gazowych, kominowych, wentylacyjnych, PPOŻ i budowlanych ${cityLocative}.`,
            provider: {
              "@id": "https://inspektomat.pl/#organization",
            },
            areaServed: {
              "@type": "City",
              name: cityName,
            },
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: `Przeglądy instalacji ${cityLocative}`,
              itemListElement: cityServices.map((service, index) => ({
                "@type": "Offer",
                position: index + 1,
                itemOffered: {
                  "@type": "Service",
                  name: service.name,
                  url: `https://inspektomat.pl${service.cityHref}`,
                },
              })),
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Strona główna",
                  item: "https://inspektomat.pl",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: cityName,
                  item: cityUrl,
                },
              ],
            },
          }),
        }}
      />

      <Banner title={`Przegląd instalacji ${cityLocative}`} />

      <section className="bg-gray-50 pt-6 pb-8 sm:pt-8 sm:pb-10">
        <MarginLayout>
          <SolutionSelector
            items={cityServices.map((s) => ({
              icon: s.icon,
              label: s.shortName,
              href: s.cityHref,
            }))}
            gridClassName="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4"
          />
        </MarginLayout>

        <MarginLayout className="mt-8">
          <div className="rounded-2xl border border-sky-100 bg-white px-6 py-6 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900">
              Usługi dostępne {cityLocative}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-500">
              Z tej strony przejdziesz tylko do aktywnych ofert lokalnych albo do głównych landingów usług, które nie mają osobnych podstron miejskich.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {cityServices.map((service) => (
                <ArrowLinkButton
                  key={service.slug}
                  href={service.cityHref}
                  label={service.name}
                  description={service.cityPages ? "oferty w mieście" : "strona usługi"}
                />
              ))}
            </div>
          </div>
        </MarginLayout>
      </section>

      <section className="py-12 sm:py-16">
        <MarginLayout>
          <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
            <FadeIn direction="left" duration={0.6}>
              <div className="overflow-hidden rounded-xl shadow-md">
                <PolandMap
                  cities={allCityMarkers}
                  center={[52.1, 19.4]}
                  zoom={6}
                  height="400px"
                  highlightedCityName={cityName}
                  onCityClick={handleCityClick}
                />
                <div className="bg-gray-50 px-4 py-2 text-xs text-gray-400">
                  {cityData.mapCaption}
                </div>
              </div>
            </FadeIn>

            <FadeIn direction="right" duration={0.6} delay={0.1}>
              <div className="text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-gray-900 sm:text-3xl">
                  Co warto wiedzieć {cityLocative}
                </h2>

                <p className="mb-4">{cityData.descriptionPara1}</p>

                <p className="mb-4">{cityData.descriptionPara2}</p>

                <ul className="mt-6 space-y-3">
                  {[
                    `Firmy z bazy działają na terenie całego miasta ${cityName}`,
                    "Porównujesz oferty wielu wykonawców w jednym miejscu",
                    "Sprawdzasz opinie przed wyborem konkretnej firmy",
                    "Wysyłasz jedno zapytanie do wielu firm jednocześnie",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <span className="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full bg-sky-100 text-center text-xs font-bold leading-5 text-sky-700">
                        ✓
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>
        </MarginLayout>
      </section>

      <section className="border-t bg-white py-10 sm:py-12">
        <MarginLayout>
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
            {cityData.stats.map((stat) => (
              <FadeIn key={stat.label} direction="up" duration={0.5}>
                <div className="text-center">
                  <p className="text-2xl font-bold text-sky-800 sm:text-3xl">{stat.value}</p>
                  <p className="mt-1 text-sm text-gray-500">{stat.label}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </MarginLayout>
      </section>

      <section className="bg-gray-50 py-10">
        <MarginLayout>
          <FadeIn direction="up" duration={0.5}>
            <h2 className="mb-4 text-xl font-bold text-gray-900">
              Obsługiwane dzielnice
            </h2>
            <div className="flex flex-wrap gap-2">
              {cityData.areas.map((area) => (
                <span
                  key={area}
                  className="rounded-full border border-gray-200 bg-white px-3 py-1 text-sm text-gray-600"
                >
                  {area}
                </span>
              ))}
            </div>
          </FadeIn>
        </MarginLayout>
      </section>

      {cityData.nearbyMunicipalities.length > 0 && (
        <section className="border-t bg-white py-10">
          <MarginLayout>
            <FadeIn direction="up" duration={0.5}>
              <h2 className="mb-2 text-xl font-bold text-gray-900">
                Obsługujemy też okoliczne miejscowości
              </h2>
              <p className="mb-5 text-sm text-gray-500">
                Firmy z naszej bazy realizują przeglądy również poza granicami {cityName}.
              </p>
              <div className="flex flex-wrap gap-2">
                {cityData.nearbyMunicipalities.map((municipality) => (
                  <span
                    key={municipality}
                    className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-sm text-sky-700"
                  >
                    {municipality}
                  </span>
                ))}
              </div>
            </FadeIn>
          </MarginLayout>
        </section>
      )}

      <section className="border-t bg-white py-6 sm:py-8">
        <MarginLayout>
          <SafetyFact fact={fact} />
        </MarginLayout>
      </section>

      {faqs.length > 0 && (
        <section className="bg-gray-50 py-12 sm:py-16">
          <MarginLayout>
            <FadeIn direction="up" duration={0.5}>
              <h2 className="mb-8 text-2xl font-bold text-gray-900 sm:text-3xl">
                Najczęstsze pytania — przeglądy instalacji {cityLocative}
              </h2>
              <div className="divide-y divide-sky-100 rounded-xl border border-sky-100 bg-white">
                {faqs.map(({ q, a }, index) => {
                  const isOpen = openFaqIndex === index;

                  return (
                    <div key={q} className="px-6 py-4">
                      <button
                        type="button"
                        className="flex w-full items-start justify-between gap-4 text-left font-medium text-gray-900"
                        onClick={() =>
                          setOpenFaqIndex((current) =>
                            current === index ? null : index
                          )
                        }
                        aria-expanded={isOpen}
                      >
                        <span>{q}</span>
                        <span
                          className={`mt-0.5 flex-shrink-0 text-gray-400 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                            isOpen ? "rotate-45" : ""
                          }`}
                        >
                          +
                        </span>
                      </button>
                      <div
                        className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                        }`}
                        aria-hidden={!isOpen}
                      >
                        <div className="min-h-0 overflow-hidden">
                          <p
                            className={`text-sm leading-relaxed text-gray-600 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                              isOpen
                                ? "mt-3 translate-y-0 opacity-100"
                                : "-translate-y-1 opacity-0"
                            }`}
                          >
                            {a}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </FadeIn>
          </MarginLayout>
        </section>
      )}

    </>
  );
};

CityPage.getLayout = (page: ReactElement) => {
  const props = page.props as CityPageProps;

  return (
    <MainLayout>
      <NextSeo
        title={`Przeglądy instalacji ${props.cityLocative}`}
        description={`Profesjonalne przeglądy instalacji elektrycznych, gazowych i czujników dymu ${props.cityLocative}. Porównaj lokalne firmy i umów wizytę online.`}
        noindex={false}
        openGraph={{
          title: `Przeglądy instalacji ${props.cityLocative}`,
          description:
            `Przeglądy instalacji elektrycznych, gazowych i czujników dymu ${props.cityLocative}.`,
          url: `https://inspektomat.pl/${props.citySlug}/`,
        }}
      />
      {page}
    </MainLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: CITIES.map((city) => ({
      params: { slug: city.slug },
    })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<CityPageProps> = async ({ params }) => {
  const cityParam = typeof params?.slug === "string" ? params.slug : "";
  const city = getCityBySlug(cityParam);

  if (!city) {
    return { notFound: true };
  }

  return {
    props: {
      cityName: city.name,
      cityLocative: city.nameLocative,
      citySlug: city.slug,
      fact: getFactForPage(city.slug),
      availableCityServiceSlugs: getAvailableCityServices(city.slug).map(
        (service) => service.slug
      ),
    },
  };
};

export default CityPage;
